/* global ADV */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import formatAsCurrency from '../CommonUtils/formatAsCurrency';
import {
  showDashboard as showDashboardSelector,
  adviserFirstNameSelector,
} from '../AdviserArea/AdviserAreaSelector';
import { useDashboardQuery } from './api/useDashboardQuery';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Stat } from '../CommonComponents/OPL/Stat/Stat';
import { Text } from '../CommonComponents/OPL/Text/Text';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Card } from '../CommonComponents/OPL/Card/Card';
import { DashboardTable } from './components/DashboardTable';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { DashboardLoading } from './Dashboard.loading';

export const Dashboard = () => {
  const adviserFirstName = useSelector(adviserFirstNameSelector);
  const showDashboard = useSelector(showDashboardSelector);

  const { data, isLoading, isError, refetch } = useDashboardQuery();

  let helpKey;

  useEffect(() => {
    if (ADV.util.env('isDemoMode')) {
      window.history.back();

      return () => {};
    }

    if (!showDashboard) {
      ADV.util.Router.go2('ClientSearch', {});

      return () => {};
    }

    helpKey = ADV.util.setHelp(ADV.HelpDescriptors.dashboard);

    refetch();

    return () => ADV.util.unsetHelp(helpKey);
  }, []);

  useDocumentTitle('Dashboard');

  if (isLoading) {
    return <DashboardLoading />;
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" p={96}>
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="24">
        <Text as="h1" fontSize="h2" fontWeight={400} color="neutral.900">
          Welcome, {adviserFirstName}.
        </Text>
        <Button variant="link" leftIcon="fa fa-sync-alt" onClick={() => refetch()}>
          Refresh
        </Button>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb="24"
        css={{ gap: 8 }}
      >
        <Card p="24">
          <Stat size="small" label="Completed Plans" value={data.completedPlans.toString()} />
        </Card>
        <Card p="24">
          <Stat size="small" label="Protection Gaps" value={data.protectionGaps.toString()} />
        </Card>
        <Card p="24">
          <Stat size="small" label="Held Away Accounts" value={data.heldAwayAccounts.toString()} />
        </Card>
        <Card p="24">
          <Stat
            size="small"
            label="Held Away Assets"
            value={formatAsCurrency(data.heldAwayAssets)}
          />
        </Card>
        <Card p="24">
          <Stat
            size="small"
            label="Potential AUM Fees"
            value={formatAsCurrency(data.potentialAumFees)}
          />
        </Card>
      </Box>

      <Box display="flex" justifyContent="space-between" flexWrap="wrap">
        <DashboardTable
          title="Top Clients by Investable Assets"
          headers={['Name', 'Total Assets']}
          data={data.topClientsByInvestableAssets}
        />
        <DashboardTable
          title="Top Asset Gathering Opportunities"
          headers={['Name', 'Total Assets']}
          data={data.topAssetGatheringOpportunities}
        />
        <DashboardTable
          title="Top Life Insurance Opportunities"
          headers={['Name', 'Coverage Gap']}
          data={data.topLifeInsuranceOpportunities}
        />
      </Box>
    </>
  );
};
